/* eslint-disable spire/export-styles */
import logger from "@insite/client-framework/Logger";
import { post } from "@insite/client-framework/Services/ApiService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Modal from "@insite/mobius/Modal";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { formatPhoneNumber } from "@tKgpco/Common/Utilities/formatPhoneNumber";
import {
    CallbackType,
    ErrorObjectType,
    InputObjectType,
    validate,
    ValidatorRuleType,
} from "@tKgpco/Common/Utilities/validate";
import { getWORData, getWORDeliveryInfoData, setDeliveryContactInfoInLocalStorage } from "@tKgpco/Services/WORService";
import { useWORContext } from "@tKgpco/Widgets/WOR/WORCheckoutReviewSubmitPage";
import * as React from "react";
import { useContext, useMemo, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    userLastName: state.context.session.lastName,
    userFirstName: state.context.session.firstName,
    userEmail: state.context.session.email,
});
type Props = ReturnType<typeof mapStateToProps> & WidgetProps;
export interface ValidatorConfigType {
    [key: string]: ValidatorRuleType;
}

export interface FormErrorsStateType {
    [key: string]: string | undefined;
}
const worValidatorConfig: ValidatorConfigType = {
    name: {
        required: true,
        minLength: 2,
        label: "Name",
    },
    email: {
        required: true,
        regex: /^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/,
        label: "Email Address",
    },
    phone: {
        required: true,
        label: "Phone Number",
        maxLength: 12,
        maxLengthMsg: "Please Enter Valid Phone Number",
    },
    ccemail: {
        required: false,
        regex: /^([a-zA-Z0-9_\-\.]+@[a-zA-Z0-9_\-\.]+\.[a-zA-Z]{2,5})(,\s*[a-zA-Z0-9_\-\.]+@[a-zA-Z0-9_\-\.]+\.[a-zA-Z]{2,5})*$|^$/,
        label: "CC Email Address",
    },
};

interface EmailModalStyles {
    textField?: TextFieldPresentationProps;
}

const styles: EmailModalStyles = {
    textField: {
        cssOverrides: {
            formField: css`
                margin-bottom: 8px;
            `,
        },
    },
};

const WORShipMaterialButton: React.FunctionComponent<Props> = ({
    fields,
    userEmail,
    userFirstName,
    userLastName,
}: Props) => {
    const [showEmailInfo, setShowEmailInfo] = useState(false);
    const [emailInfoState, setEmailInfoState] = useState<any>({});
    const [emailInfoErrors, setEmailInfoErrors] = useState<any>({ email: "", name: "", phone: "" });
    const history = useHistory();
    const toasterContext = useContext(ToasterContext);
    const { worData: worReviewPageData, worDispatch } = useWORContext();
    const [buttonClicked, setButtonClicked] = useState(false);
    const userName = `${userFirstName} ${userLastName}`;
    const [disableShipMaterialButton, setDisableShipMaterialButton] = useState(false);
    const isDisabled = useMemo(() => {
        return (
            !worReviewPageData.requestedDeliveryDate ||
            !worReviewPageData.selectedCarrier ||
            !worReviewPageData.selectedShipVia ||
            buttonClicked
        );
    }, [worReviewPageData]);

    const isEmailInfoBtnDisabled = useMemo(() => Object.keys(emailInfoErrors).length > 0, [emailInfoErrors]);

    const removeKey = (formErrorObject: FormErrorsStateType, deleteKey: string) => {
        return Object.assign(
            {},
            ...Object.entries(formErrorObject)
                .filter(([k]) => k !== deleteKey)
                .map(([k, v]) => ({ [k]: v })),
        );
    };

    const validateHandler = (input: InputObjectType, customValidation?: CallbackType): void => {
        const inputObject: InputObjectType = {
            name: input.name,
            value: input.value,
        };
        const validatorConfig = worValidatorConfig[input.name as keyof ValidatorConfigType];
        const isValidate: ErrorObjectType | undefined = validate(inputObject, validatorConfig, customValidation);
        if (isValidate === undefined) {
            delete emailInfoErrors[input.name as keyof FormErrorsStateType];
            setEmailInfoErrors(removeKey(emailInfoErrors, input.name));
        } else {
            setEmailInfoErrors((prevState: any) => {
                return {
                    ...prevState,
                    [input.name]: isValidate ? isValidate.errorMsg : isValidate,
                };
            });
        }
        if (!validatorConfig.required && input.value === "") {
            setEmailInfoErrors(removeKey(emailInfoErrors, input.name));
        }
    };

    const shipMaterial = async () => {
        const worData = getWORData();
        const worDeliveryInfoFromStorage = getWORDeliveryInfoData().updated;
        setButtonClicked(true);
        setShowEmailInfo(false);
        const {
            shippingAddress: {
                address1,
                address2,
                address3,
                address4,
                attention,
                city,
                companyName,
                country,
                state,
                postalCode,
            },
            formState,
            orderShipto,
        } = worData;

        const company = worData.useOneTimeAddress
            ? worData.formState.companyName
            : worData.useOrderShipto
            ? worData.orderShipto.stCompanyName
            : worData.shippingAddress.companyName;
        const tomorrow = new Date(worReviewPageData.requestedDeliveryDate);
        tomorrow.setDate(tomorrow.getDate() + 1);

        let params: any = {
            DataAreaId: "KGP",
            SalesId: worData.orderNumber,
            WONumber: worData.woNumber,
            RequisitionNumber: worData.requisitionNumber,
            CustomerPO: worData.customerPO,
            Notes: worReviewPageData.orderNotes,
            HeaderNotes: worData.HeaderNotes,
            Location: "",
            RSDate: worReviewPageData.requestedDeliveryDate,
            RADate: tomorrow,
            DlvMode: worReviewPageData.selectedShipVia.serviceCode, // worReviewPageData.selectedCarrier.carrierName,
            DeliveryOption: "", // worReviewPageData.selectedShipVia.serviceCode,
            SameDeliveryAddress: false,
            UserName: emailInfoState.name,
            Phone: emailInfoState.phone,
            UserEmailAddress: emailInfoState.email,
            CCAddress: emailInfoState.ccemail,
            ShipmentAccessorials: worReviewPageData?.selectedShipVia?.isPickUp
                ? null
                : worReviewPageData.ShipmentAccessorials,
            companyName: company,
        };

        if (!worData.useOrderShipto && worData.useOneTimeAddress && formState) {
            params = {
                ...params,
                Country: formState.country.abbreviation,
                City: formState.city,
                Street: formState.address1,
                PostCode: formState.postalCode,
                State: formState.state?.abbreviation,
            };
        } else {
            params = {
                ...params,
                Country: worData.useOrderShipto ? country : country.abbreviation,
                City: city,
                Street: address1,
                PostCode: postalCode,
                State: worData.useOrderShipto ? state : state?.abbreviation,
            };
        }

        if (orderShipto && !worData.useOneTimeAddress) {
            params.SameDeliveryAddress =
                (orderShipto.stAddress1 === address1 &&
                    orderShipto.stCity === city &&
                    orderShipto.stPostalCode === postalCode &&
                    orderShipto.stCompanyName === companyName) ||
                worData.useOrderShipto;
        }
        const orderLines = worData.items.map((oLine: any) => ({
            ProductNumber: oLine.productNumber,
            ReleasedQty: Number(oLine.releaseQty),
            lineNum: oLine.lineNumber,
            QtyOrdered: oLine.qtyOrdered,
            ReservedQty: oLine.reservedQty,
            Description: oLine.description,
            UOM: oLine.uom,
            d365ReleasedQty: oLine.d365ReleasedQty,
            qtyShipped: oLine.qtyShipped,
            VendorPartNumber: oLine.manufacturerItem || "",
            poLineNumber: oLine.poLineNumber || "",
        }));
        params["LoadLinesList"] = orderLines;
        try {
            // const response = await post("/api/v2/WOR/PostWORData", params);
            const response = await fetch("/api/v2/WOR/PostWORData", {
                method: "POST", // or 'PUT'
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    ...params,
                    D365UserName: `${worDeliveryInfoFromStorage.firstName} ${worDeliveryInfoFromStorage.lastName}`,
                    D365Phone: worDeliveryInfoFromStorage.phone,
                    D365EmailAddress: worDeliveryInfoFromStorage.email,
                    ...(worDeliveryInfoFromStorage.address3 && {
                        ExtDeliveryNote1: worDeliveryInfoFromStorage.address3,
                    }),
                    ...(worDeliveryInfoFromStorage.address4 && {
                        ExtDeliveryNote2: worDeliveryInfoFromStorage.address4,
                    }),
                }),
            }).finally(() => {
                setDeliveryContactInfoInLocalStorage({});
            });
            if (response.status === 200) {
                toasterContext.addToast({ body: "Request sent successfully", messageType: "success" });
            }
            if (response.status === 500) {
                // const result = await response.json();
                toasterContext.addToast({ body: siteMessage("worShipApiMessage"), messageType: "danger" });
                // if(result.exceptionMessage) {
                // } else {
                //     toasterContext.addToast({ body: "Something went wrong", messageType: "danger" });
                // }
            }
            setTimeout(() => {
                if (response.status === 200) {
                    const url = `${window.location.protocol}//${window.location.host}/MyAccount/Orders/Details?orderNumber=${worData.orderNumber}`;
                    window.location.href = url;
                }
            }, 3000);
        } catch (error) {
            toasterContext.addToast({ body: siteMessage("worShipApiMessage"), messageType: "danger" });
            logger.error(error);
        }
    };

    const phoneChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setEmailInfoState({ ...emailInfoState, [name]: formatPhoneNumber(value) });
        validateHandler({ name, value: formatPhoneNumber(value) });
    };

    const handleChangeEmailInfo = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setEmailInfoState({ ...emailInfoState, [name]: value });
        validateHandler({ name, value });
    };
    React.useEffect(() => {
        setEmailInfoState({
            ...emailInfoState,
            name: userName,
            email: userEmail,
        });
        setEmailInfoErrors({ phone: "" });
    }, []);
    return (
        <>
            <GridContainer>
                <GridItem
                    width={12}
                    css={css`
                        background-color: #f1f6fb;
                    `}
                >
                    <Typography>
                        The materials designated for release will be shipped to shipping address. If you select Ship to
                        One-Time Address, you can override the default shipping address.
                    </Typography>
                </GridItem>
                <GridItem width={12}>
                    <Button
                        css={css`
                            width: 100%;
                            border-radius: 1.5em;
                        `}
                        onClick={() => {
                            setDisableShipMaterialButton(true);
                            setShowEmailInfo(true);
                        }}
                        disabled={isDisabled || disableShipMaterialButton}
                    >
                        Ship Material
                    </Button>
                </GridItem>
                <GridItem width={12}>
                    <Button
                        css={css`
                            width: 100%;
                            border-radius: 1.5em;
                        `}
                        onClick={() => history.replace("/WOR-Checkout-Shipping")}
                        variant="secondary"
                    >
                        Back
                    </Button>
                </GridItem>
            </GridContainer>
            <Modal
                headline={translate("Email Information")}
                handleClose={() => {
                    setShowEmailInfo(false);
                    setDisableShipMaterialButton(false);
                }}
                isOpen={showEmailInfo}
            >
                <Typography
                    as="p"
                    css={css`
                        margin-bottom: 8px;
                        border-bottom: 1px solid #a0a0a0;
                    `}
                >
                    * Required Fields
                </Typography>

                <TextField
                    label="Enter your name"
                    name="name"
                    value={emailInfoState.name || ""}
                    error={!!emailInfoErrors.name && emailInfoErrors.name}
                    onChange={handleChangeEmailInfo}
                    onBlur={e => validateHandler({ name: e.target.name, value: e.target.value })}
                    required={true}
                    {...styles.textField}
                />
                <TextField
                    label="Enter your phone number"
                    name="phone"
                    value={emailInfoState.phone || ""}
                    error={!!emailInfoErrors.phone && emailInfoErrors.phone}
                    onChange={phoneChangeHandler}
                    onBlur={e => validateHandler({ name: e.target.name, value: formatPhoneNumber(e.target.value) })}
                    required={true}
                    {...styles.textField}
                />
                <TextField
                    label="Enter your email"
                    name="email"
                    value={emailInfoState.email || ""}
                    error={!!emailInfoErrors.email && emailInfoErrors.email}
                    onChange={handleChangeEmailInfo}
                    onBlur={e => validateHandler({ name: e.target.name, value: e.target.value })}
                    required={true}
                    {...styles.textField}
                />
                <TextField
                    label="Enter your carbon copy email"
                    name="ccemail"
                    value={emailInfoState.ccemail || ""}
                    onChange={handleChangeEmailInfo}
                    {...styles.textField}
                />
                <Button
                    css={css`
                        width: 100%;
                        max-width: 300px;
                        border-radius: 1.5em;
                    `}
                    onClick={shipMaterial}
                    disabled={isEmailInfoBtnDisabled}
                >
                    Ship Material
                </Button>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(WORShipMaterialButton),
    definition: {
        group: "Basic",
        icon: "heading",
    },
};

export default widgetModule;
